import React, { useState } from 'react';
import { BarChart, Bar, PieChart, Pie, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell, ResponsiveContainer } from 'recharts';
import NgoLayout from "../../../../layouts/Ngo/NgoLayout";
import '../../../../assets/css/Ngo/controlpanelCSS/NgoReports.css';

const NGOReports = () => {
  const [timeFilter, setTimeFilter] = useState('monthly');

  // Data for different time periods
  const dataByPeriod = {
    monthly: {
      fundUtilization: [
        { name: 'Fee', amount: 450000 },
        { name: 'Books', amount: 320000 },
        { name: 'Uniform', amount: 280000 },
      ],
      caseStatus: [
        { name: 'New Cases', value: 45, color: '#667eea' },
        { name: 'In Progress', value: 78, color: '#f093fb' },
        { name: 'Completed', value: 220, color: '#38ef7d' },
      ],
      donationTrends: [
        { month: 'Jan', amount: 150000 },
        { month: 'Feb', amount: 180000 },
        { month: 'Mar', amount: 220000 },
        { month: 'Apr', amount: 195000 },
        { month: 'May', amount: 240000 },
        { month: 'Jun', amount: 265000 },
      ],
      stats: {
        totalFunds: '₹10,50,000',
        beneficiaries: '343 Cases',
        completionRate: '64.2%',
        growth: '+18.5%'
      }
    },
    quarterly: {
      fundUtilization: [
        { name: 'Fee', amount: 1350000 },
        { name: 'Books', amount: 960000 },
        { name: 'Uniform', amount: 840000 },
      ],
      caseStatus: [
        { name: 'New Cases', value: 135, color: '#667eea' },
        { name: 'In Progress', value: 234, color: '#f093fb' },
        { name: 'Completed', value: 660, color: '#38ef7d' },
      ],
      donationTrends: [
        { month: 'Q1', amount: 550000 },
        { month: 'Q2', amount: 700000 },
        { month: 'Q3', amount: 850000 },
        { month: 'Q4', amount: 1050000 },
      ],
      stats: {
        totalFunds: '₹31,50,000',
        beneficiaries: '1,029 Cases',
        completionRate: '64.2%',
        growth: '+22.3%'
      }
    },
    yearly: {
      fundUtilization: [
        { name: 'Fee', amount: 5400000 },
        { name: 'Books', amount: 3840000 },
        { name: 'Uniform', amount: 3360000 },
      ],
      caseStatus: [
        { name: 'New Cases', value: 540, color: '#667eea' },
        { name: 'In Progress', value: 936, color: '#f093fb' },
        { name: 'Completed', value: 2640, color: '#38ef7d' },
      ],
      donationTrends: [
        { month: '2019', amount: 8500000 },
        { month: '2020', amount: 9200000 },
        { month: '2021', amount: 10500000 },
        { month: '2022', amount: 11800000 },
        { month: '2023', amount: 12600000 },
      ],
      stats: {
        totalFunds: '₹1,26,00,000',
        beneficiaries: '4,116 Cases',
        completionRate: '64.2%',
        growth: '+15.8%'
      }
    }
  };

  const currentData = dataByPeriod[timeFilter];
  const totalCases = currentData.caseStatus.reduce((sum, item) => sum + item.value, 0);
  const totalFundUtilization = currentData.fundUtilization.reduce((sum, item) => sum + item.amount, 0);

  const handleExportPDF = () => {
    alert('📄 PDF Export functionality will be implemented!');
  };

  const handleExportExcel = () => {
    alert('📊 Excel Export functionality will be implemented!');
  };

  return (
    <NgoLayout>
      <div className="container-fluid ngo-reports-container">
        {/* Header Section */}
        <div className="row">
          <div className="col-12">
            <div className="ngo-reports-header">
              <div className="ngo-reports-title">
                <h1>📊 Reports & Analytics Dashboard</h1>
                <p>Track your NGO's impact and fund utilization</p>
              </div>
              <div className="ngo-reports-filters">
                <button
                  className={`ngo-filter-btn ${timeFilter === 'monthly' ? 'ngo-active' : ''}`}
                  onClick={() => setTimeFilter('monthly')}
                >
                  📅 Monthly
                </button>
                <button
                  className={`ngo-filter-btn ${timeFilter === 'quarterly' ? 'ngo-active' : ''}`}
                  onClick={() => setTimeFilter('quarterly')}
                >
                  📆 Quarterly
                </button>
                <button
                  className={`ngo-filter-btn ${timeFilter === 'yearly' ? 'ngo-active' : ''}`}
                  onClick={() => setTimeFilter('yearly')}
                >
                  📈 Yearly
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Export Section */}
        <div className="row">
          <div className="col-12">
            <div className="ngo-export-section">
              <button className="ngo-export-btn ngo-pdf-btn" onClick={handleExportPDF}>
                📄 Export PDF
              </button>
              <button className="ngo-export-btn ngo-excel-btn" onClick={handleExportExcel}>
                📊 Export Excel
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="row">
          <div className="col-lg-3 col-md-6 col-sm-6 mb-3">
            <div className="ngo-stat-card">
              <div className="ngo-stat-icon">💰</div>
              <div className="ngo-stat-info">
                <h4>Total Funds</h4>
                <p>{currentData.stats.totalFunds}</p>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6 col-sm-6 mb-3">
            <div className="ngo-stat-card">
              <div className="ngo-stat-icon">👥</div>
              <div className="ngo-stat-info">
                <h4>Beneficiaries</h4>
                <p>{currentData.stats.beneficiaries}</p>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6 col-sm-6 mb-3">
            <div className="ngo-stat-card">
              <div className="ngo-stat-icon">✅</div>
              <div className="ngo-stat-info">
                <h4>Completion Rate</h4>
                <p>{currentData.stats.completionRate}</p>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6 col-sm-6 mb-3">
            <div className="ngo-stat-card">
              <div className="ngo-stat-icon">📈</div>
              <div className="ngo-stat-info">
                <h4>Growth</h4>
                <p>{currentData.stats.growth}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Case Status & Donation Trends Row */}
        <div className="row">
          {/* Case Status Chart */}
          <div className="col-lg-6 col-md-12 mb-3">
            <div className="ngo-chart-card">
              <div className="ngo-chart-header">
                <h3>📋 Case Status Overview</h3>
                <p>Total Cases: {totalCases}</p>
              </div>
              <div className="ngo-chart-content ngo-center">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={currentData.caseStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {currentData.caseStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="ngo-legend">
                  {currentData.caseStatus.map((item, index) => (
                    <div key={index} className="ngo-legend-item">
                      <div
                        className="ngo-legend-color"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="ngo-legend-text">
                        <strong>{item.name}:</strong> {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Donation Trends Chart */}
          <div className="col-lg-6 col-md-12 mb-3">
            <div className="ngo-chart-card">
              <div className="ngo-chart-header">
                <h3>💵 Donation Trends</h3>
                <p>
                  {timeFilter === 'monthly' && '6-Month Overview'}
                  {timeFilter === 'quarterly' && 'Quarterly Overview'}
                  {timeFilter === 'yearly' && '5-Year Overview'}
                </p>
              </div>
              <div className="ngo-chart-content">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={currentData.donationTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`} />
                    <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="amount"
                      stroke="#667eea"
                      strokeWidth={3}
                      dot={{ fill: '#667eea', r: 6 }}
                      name="Donation Amount"
                    />
                  </LineChart>
                </ResponsiveContainer>
                <div className="ngo-chart-insights">
                  <div className="ngo-insight-item">
                    <span className="ngo-insight-label">📈 Trend:</span>
                    <span className="ngo-insight-value">
                      {timeFilter === 'monthly' && 'Steady growth over 6 months'}
                      {timeFilter === 'quarterly' && 'Progressive quarterly increase'}
                      {timeFilter === 'yearly' && 'Consistent annual improvement'}
                    </span>
                  </div>
                  <div className="ngo-insight-item">
                    <span className="ngo-insight-label">💡 Insight:</span>
                    <span className="ngo-insight-value">
                      {timeFilter === 'monthly' && 'Peak donation in June'}
                      {timeFilter === 'quarterly' && 'Q4 shows highest contributions'}
                      {timeFilter === 'yearly' && 'Year-over-year growth sustained'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fund Utilization Chart */}
        <div className="row">
          <div className="col-12 mb-3">
            <div className="ngo-chart-card">
              <div className="ngo-chart-header">
                <h3>💰 Fund Utilization by Category</h3>
                <p>Total Distribution: ₹{totalFundUtilization.toLocaleString()}</p>
              </div>
              <div className="ngo-chart-content">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={currentData.fundUtilization}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`} />
                    <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                    <Legend />
                    <Bar dataKey="amount" fill="#667eea" radius={[8, 8, 0, 0]} name="Amount" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </NgoLayout>
  );
};

export default NGOReports;